import { useEffect, useRef, useState } from "react";
import { Loader2, Box } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getProjectAsset3d,
  objectUrlFromAsset,
  type StoredAsset3d,
} from "@/lib/prometheusAsset3dStore";

export type ArtifactGlbViewerProps = {
  projectId: string;
  className?: string;
  /** Remount / refresh key */
  rev?: number;
};

/**
 * Lightweight Three.js preview for imported GLB/GLTF/STL (Artifact).
 */
export function ArtifactGlbViewer({ projectId, className, rev = 0 }: ArtifactGlbViewerProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<"loading" | "empty" | "ready" | "error">("loading");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [fileLabel, setFileLabel] = useState<string | null>(null);

  useEffect(() => {
    let disposed = false;
    let objectUrl: string | null = null;
    let renderer: import("three").WebGLRenderer | null = null;
    let raf = 0;
    let controls: { dispose: () => void; update: () => void } | null = null;
    let onResize: (() => void) | null = null;

    const teardown = () => {
      disposed = true;
      cancelAnimationFrame(raf);
      if (onResize) window.removeEventListener("resize", onResize);
      controls?.dispose();
      controls = null;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      renderer?.dispose();
      renderer = null;
      if (hostRef.current) hostRef.current.replaceChildren();
    };

    const run = async () => {
      setStatus("loading");
      setErrorMsg(null);
      setFileLabel(null);

      let asset: StoredAsset3d | null = null;
      try {
        asset = await getProjectAsset3d(projectId);
      } catch (e) {
        if (!disposed) {
          setStatus("error");
          setErrorMsg(e instanceof Error ? e.message : "No se pudo leer IndexedDB");
        }
        return;
      }

      if (disposed) return;
      if (!asset) {
        setStatus("empty");
        return;
      }

      setFileLabel(`${asset.fileName} · ${(asset.byteLength / 1024).toFixed(0)} KB`);
      const host = hostRef.current;
      if (!host) return;

      const THREE = await import("three");
      const { OrbitControls } = await import("three/examples/jsm/controls/OrbitControls.js");
      const { GLTFLoader } = await import("three/examples/jsm/loaders/GLTFLoader.js");
      const { STLLoader } = await import("three/examples/jsm/loaders/STLLoader.js");

      if (disposed || !hostRef.current) return;

      const width = host.clientWidth || 320;
      const height = host.clientHeight || 220;

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x071018);

      const camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 500);
      camera.position.set(1.8, 1.4, 2.2);

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(width, height);
      host.replaceChildren(renderer.domElement);

      scene.add(new THREE.HemisphereLight(0xb1e1ff, 0x080820, 1.1));
      const dir = new THREE.DirectionalLight(0xffffff, 1.2);
      dir.position.set(3, 5, 2);
      scene.add(dir);
      scene.add(new THREE.AmbientLight(0xffffff, 0.25));
      scene.add(new THREE.GridHelper(4, 16, 0x1e3a5f, 0x0f1c2e));

      const orbit = new OrbitControls(camera, renderer.domElement);
      orbit.enableDamping = true;
      orbit.target.set(0, 0.3, 0);
      controls = orbit;

      objectUrl = objectUrlFromAsset(asset);
      const url = objectUrl;

      const frameObject = (root: import("three").Object3D) => {
        const box = new THREE.Box3().setFromObject(root);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        root.position.sub(center);
        const maxDim = Math.max(size.x, size.y, size.z, 0.001);
        root.scale.setScalar(1.6 / maxDim);
        orbit.target.set(0, 0.15, 0);
        camera.position.set(1.6, 1.2, 2.0);
        camera.lookAt(0, 0.15, 0);
      };

      try {
        if (asset.format === "stl") {
          const geom = await new STLLoader().loadAsync(url);
          const mesh = new THREE.Mesh(
            geom,
            new THREE.MeshStandardMaterial({
              color: 0x38bdf8,
              metalness: 0.15,
              roughness: 0.55,
            })
          );
          scene.add(mesh);
          frameObject(mesh);
        } else {
          const gltf = await new GLTFLoader().loadAsync(url);
          scene.add(gltf.scene);
          frameObject(gltf.scene);
        }
        if (!disposed) setStatus("ready");
      } catch (e) {
        if (!disposed) {
          setStatus("error");
          setErrorMsg(e instanceof Error ? e.message : "Error al cargar el modelo");
        }
        return;
      }

      onResize = () => {
        if (!hostRef.current || !renderer) return;
        const w = hostRef.current.clientWidth;
        const h = hostRef.current.clientHeight;
        camera.aspect = w / Math.max(h, 1);
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      };
      window.addEventListener("resize", onResize);

      const tick = () => {
        if (disposed || !renderer) return;
        controls?.update();
        renderer.render(scene, camera);
        raf = requestAnimationFrame(tick);
      };
      tick();
    };

    void run();
    return teardown;
  }, [projectId, rev]);

  return (
    <div
      className={cn(
        "relative rounded-xl border border-sky-500/30 bg-[#071018] overflow-hidden",
        className
      )}
    >
      <div ref={hostRef} className="h-52 w-full" />
      {status === "loading" ? (
        <div className="absolute inset-0 flex items-center justify-center gap-2 text-[11px] text-sky-300/90 font-mono-tech bg-[#071018]/80">
          <Loader2 className="w-4 h-4 animate-spin" />
          Cargando modelo…
        </div>
      ) : null}
      {status === "empty" ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-[11px] text-muted-foreground font-mono-tech px-4 text-center pointer-events-none">
          <Box className="w-5 h-5 text-sky-500/60 mb-1" />
          Sin GLB/STL importado — usa «Importar modelo» arriba.
        </div>
      ) : null}
      {status === "error" ? (
        <div className="absolute inset-0 flex items-center justify-center text-[11px] text-red-300 font-mono-tech px-4 text-center bg-[#071018]/90">
          {errorMsg || "Error de preview"}
        </div>
      ) : null}
      {fileLabel && status === "ready" ? (
        <p className="absolute bottom-1.5 left-2 right-2 text-[9px] font-mono-tech text-sky-300/80 truncate pointer-events-none">
          {fileLabel}
        </p>
      ) : null}
    </div>
  );
}
